// ============================================================================
// NavbarContext — Lets pages inject title + actions into the shared navbar
//
// The context, its types and the hook. NO COMPONENT lives here: a file that
// exports a component alongside anything else breaks Fast Refresh, and this
// one is imported by 28 modules. The provider is NavbarProvider.tsx, which is
// imported by four -- so the split falls on the side that costs least.
// ============================================================================

import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

export interface NavbarMenuItem {
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  divider?: boolean
  color?: string
}

export interface NavbarState {
  title: string | null
  titleContent: ReactNode | null
  subtitle: ReactNode | null
  actions: ReactNode | null
  menuItems: NavbarMenuItem[]
  heightOffset: number
  /** Width of a full-height side panel the page renders between the nav rail
   *  and its content. The navbar starts to the right of it, so the panel can
   *  run to the top of the window instead of beginning under the navbar.
   *  Pages without one leave it at 0 and are unaffected. */
  panelOffset: number
  /** The page's own content measure. When set, the navbar centres its title
   *  block at this width instead of filling its flex slot, so the title lines
   *  up with the column beneath it. */
  contentMaxWidth: number | null
  /** Width of a full-height side panel the page opens on the RIGHT (the
   *  calendar's event and sleep detail drawers). The navbar ends before it, so
   *  a panel opening shrinks the whole page — navbar, sub-header and grid —
   *  rather than the grid alone sliding out from under a full-width bar.
   *  Pages without one leave it at 0. */
  panelRightOffset: number
  /** Mode accent color — tints navbar background when set */
  modeColor: string | null
  /** Mode label (e.g. "Viewing Template") -- shown as small text at bottom of navbar */
  modeLabel: string | null
  /** Hide the mode label while keeping the mode color/progress styling. */
  hideModeLabel: boolean
  /** Progress bar value (0-1) — replaces bottom border with animated progress bar */
  progress: number | null
  /** Back-chevron affordance, one knob instead of two booleans:
   *  - 'auto' (default): the layout decides — desktop chevron when the layout
   *    renders one, none on mobile (the drawer carries back there).
   *  - 'show': the page requests the chevron — it renders even where the
   *    layout defaults it off (public-share layouts hardcode it off) and on
   *    mobile too (the public-share preview renders no drawer, so the chevron
   *    is its only exit). Hidden when going back leads nowhere (anonymous
   *    viewer, no history): the authenticated fallback would be a login wall.
   *  - 'hide': the page owns its exit (discard / save) — never render the
   *    chevron, even where the layout would. */
  backButton: 'auto' | 'show' | 'hide'
  /** Show the athlete selector pill (for coaches) -- only relevant on Calendar/Views */
  showAthleteSelector: boolean
}

interface NavbarContextValue extends NavbarState {
  setNavbar: (state: Partial<NavbarState>) => void
  resetNavbar: () => void
}

export const defaultState: NavbarState = { title: null, titleContent: null, subtitle: null, actions: null, menuItems: [], heightOffset: 0, panelOffset: 0, panelRightOffset: 0, contentMaxWidth: null, modeColor: null, modeLabel: null, hideModeLabel: false, progress: null, backButton: 'auto', showAthleteSelector: false }

export const NavbarContext = createContext<NavbarContextValue>({
  ...defaultState,
  setNavbar: () => {},
  resetNavbar: () => {},
})

export function useNavbar() {
  return useContext(NavbarContext)
}
